/**
 * StyleSelector Component
 *
 * Multi-select for style presets with category filtering.
 * Shows style chips that can be toggled on/off.
 * Features hover preview zoom and virtual scrolling for performance.
 */

import { useReducer, useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import { Palette, X, ChevronDown, Check, ImageOff, Heart, ZoomIn } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { imageGenerationAPI } from '../../services/imageGenerationAPI';
import { buildApiUrl } from '../../services/urlResolver';
import { STORAGE_KEYS } from '../../constants/storage';
import { FOOOCUS_ONLY_STYLES } from '../../constants/imageSource';
import { styleSelectorReducer, initialStyleSelectorState } from './styleSelectorReducer';

// Number of items to load per batch
const ITEMS_PER_BATCH = 24;

/**
 * StylePreviewImage Component
 * Shows style preview thumbnail with loading state, fallback, and hover zoom
 */
function StylePreviewImage({
  previewUrl,
  styleName,
  isSelected,
  onHover,
  onLeave,
  onExpand,
}: {
  previewUrl?: string;
  styleName: string;
  isSelected: boolean;
  onHover?: (url: string, rect: DOMRect) => void;
  onLeave?: () => void;
  onExpand?: (url: string) => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Build full URL from relative path
  const fullUrl = previewUrl ? buildApiUrl(previewUrl) : null;

  const handleLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (fullUrl && imgRef.current && onHover) {
      const rect = imgRef.current.getBoundingClientRect();
      onHover(fullUrl, rect);
    }
  }, [fullUrl, onHover]);

  const handleMouseLeave = useCallback(() => {
    onLeave?.();
  }, [onLeave]);

  // Click thumbnail to open the full-size lightbox (without toggling selection)
  const handleExpandClick = useCallback(
    (e: React.MouseEvent) => {
      if (fullUrl && onExpand) {
        e.stopPropagation();
        e.preventDefault();
        onExpand(fullUrl);
      }
    },
    [fullUrl, onExpand]
  );

  if (!fullUrl || hasError) {
    return (
      <div
        className={`w-10 h-10 flex-shrink-0 rounded flex items-center justify-center ${
          isSelected ? 'bg-primary-900/40' : 'bg-white/5'
        }`}
      >
        <ImageOff className="w-4 h-4 text-white/30" />
      </div>
    );
  }

  return (
    <div
      ref={imgRef}
      className="group/thumb w-10 h-10 flex-shrink-0 rounded overflow-hidden relative cursor-zoom-in"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleExpandClick}
      role="button"
      aria-label={`Enlarge ${styleName}`}
    >
      {isLoading && <div className="absolute inset-0 bg-white/5 animate-pulse" />}
      <img
        src={fullUrl}
        alt={styleName}
        className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
      {/* Zoom affordance on hover */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity">
        <ZoomIn className="w-4 h-4 text-white" />
      </div>
    </div>
  );
}

/**
 * HoverPreview Component
 * Shows enlarged preview image when hovering over a style thumbnail
 */
function HoverPreview({
  imageUrl,
  styleName,
  position,
}: {
  imageUrl: string;
  styleName: string;
  position: { x: number; y: number };
}) {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -110%)',
      }}
    >
      <div className="bg-black/90 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 p-2 animate-in zoom-in-95 duration-150">
        <div className="relative w-64 h-64 rounded-lg overflow-hidden bg-black/30">
          {!isLoaded && (
            <div className="overlay-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <img
            src={imageUrl}
            alt={styleName}
            className={`w-full h-full object-cover transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
          />
        </div>
        <p className="text-center text-sm font-medium mt-2 text-white/80 break-words max-w-[16rem]">
          {styleName}
        </p>
      </div>
    </div>
  );
}

/**
 * StyleLightbox Component
 * Full-size, centered preview opened on click. Shows the large image plus the
 * full style name and category. Dismissible via the close button, backdrop click, or ESC.
 */
function StyleLightbox({
  imageUrl,
  styleName,
  category,
  onClose,
}: {
  imageUrl: string;
  styleName: string;
  category: string;
  onClose: () => void;
}) {
  const [isLoaded, setIsLoaded] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={styleName}
    >
      <div
        className="relative max-w-lg w-full bg-secondary-800 rounded-xl shadow-2xl border border-white/10 overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/50 text-white/80 hover:bg-black/70 hover:text-white transition-colors"
          aria-label="Close preview"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Large image */}
        <div className="relative w-full bg-black/30 flex items-center justify-center">
          {!isLoaded && (
            <div className="overlay-center">
              <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <img
            src={imageUrl}
            alt={styleName}
            className={`w-full max-h-[70vh] object-contain transition-opacity duration-200 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
          />
        </div>

        {/* Name + category */}
        <div className="p-4 border-t border-white/10">
          <p className="text-base font-semibold text-white break-words">{styleName}</p>
          {category && (
            <span className="mt-1 inline-block text-xs text-primary-300 bg-primary-900/30 border border-primary-500/30 rounded-full px-2 py-0.5 capitalize">
              {category}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface StyleSelectorProps {
  selectedStyles: string[];
  onChange: (styles: string[]) => void;
  maxStyles?: number;
  disabled?: boolean;
  /** Current provider - used to hide Fooocus-only styles for other providers */
  provider?: string;
}

export const StyleSelector = memo(function StyleSelector({
  selectedStyles,
  onChange,
  maxStyles = 10,
  disabled = false,
  provider,
}: StyleSelectorProps) {
  const { t } = useTranslation();

  // Ensure selectedStyles is always an array (defensive programming)
  const safeSelectedStyles = useMemo(
    () => (Array.isArray(selectedStyles) ? selectedStyles : []),
    [selectedStyles]
  );

  const [state, dispatch] = useReducer(styleSelectorReducer, initialStyleSelectorState);

  const {
    styles,
    categories,
    activeCategory,
    isExpanded,
    searchQuery,
    isLoading,
    showOnlyFavorites,
    visibleCount,
    hoverPreview,
    lightbox,
  } = state;

  // Favorites state - use useLocalStorage for cross-tab sync and consistency
  const [favorites, setFavorites] = useLocalStorage<string[]>(STORAGE_KEYS.FOOOCUS_FAVORITES, []);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load styles from API
  useEffect(() => {
    const loadStyles = async () => {
      try {
        const response = await imageGenerationAPI.getStyles();
        if (response.success && response.data) {
          // Defensive: ensure we always have arrays
          const stylesData = response.data.styles;
          const catData = response.data.categories;
          dispatch({
            type: 'STYLES_LOADED',
            payload: {
              styles: Array.isArray(stylesData) ? stylesData : [],
              categories: Array.isArray(catData) ? catData : [],
            },
          });
        }
      } catch (error) {
        console.error('Failed to load styles:', error);
      } finally {
        dispatch({ type: 'SET_IS_LOADING', payload: false });
      }
    };

    loadStyles();
  }, []);

  // Reset visible count when filter changes
  useEffect(() => {
    dispatch({ type: 'RESET_VISIBLE_COUNT', payload: ITEMS_PER_BATCH });
  }, [activeCategory, searchQuery, showOnlyFavorites]);

  // Filter styles by category, search, favorites, and provider
  const filteredStyles = useMemo(() => {
    let filtered = styles;

    // Hide Fooocus-only styles for non-Fooocus providers
    if (provider && provider !== 'fooocus') {
      filtered = filtered.filter(
        (s) => !FOOOCUS_ONLY_STYLES.includes(s.name as (typeof FOOOCUS_ONLY_STYLES)[number])
      );
    }

    if (activeCategory) {
      filtered = filtered.filter((s) => s.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) => s.name.toLowerCase().includes(query) || s.category.toLowerCase().includes(query)
      );
    }

    if (showOnlyFavorites) {
      filtered = filtered.filter((s) => favorites.includes(s.name));
    }

    return filtered;
  }, [styles, activeCategory, searchQuery, showOnlyFavorites, favorites, provider]);

  // Get visible styles (virtual scrolling)
  const visibleStyles = useMemo(() => {
    return filteredStyles.slice(0, visibleCount);
  }, [filteredStyles, visibleCount]);

  // Handle scroll to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollThreshold = 100; // pixels from bottom to trigger load

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        if (visibleCount < filteredStyles.length) {
          const newCount = Math.min(visibleCount + ITEMS_PER_BATCH, filteredStyles.length);
          dispatch({ type: 'SET_VISIBLE_COUNT', payload: newCount });
        }
      }
    },
    [filteredStyles.length, visibleCount]
  );

  const toggleStyle = (styleName: string) => {
    if (safeSelectedStyles.includes(styleName)) {
      onChange(safeSelectedStyles.filter((s) => s !== styleName));
    } else if (safeSelectedStyles.length < maxStyles) {
      onChange([...safeSelectedStyles, styleName]);
    }
  };

  const removeStyle = (styleName: string) => {
    onChange(safeSelectedStyles.filter((s) => s !== styleName));
  };

  const clearAllStyles = () => {
    onChange([]);
  };

  // Toggle favorite status for a style - useLocalStorage handles persistence
  const toggleFavorite = useCallback(
    (styleName: string, e: React.MouseEvent) => {
      e.stopPropagation(); // Don't trigger style selection
      setFavorites((prev) =>
        prev.includes(styleName) ? prev.filter((f) => f !== styleName) : [...prev, styleName]
      );
    },
    [setFavorites]
  );

  // Handle hover preview with delay
  const handleImageHover = useCallback((url: string, rect: DOMRect, styleName: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Add small delay to avoid flickering
    hoverTimeoutRef.current = setTimeout(() => {
      dispatch({
        type: 'SET_HOVER_PREVIEW',
        payload: {
          url,
          name: styleName,
          position: {
            x: rect.left + rect.width / 2,
            y: rect.top,
          },
        },
      });
    }, 200);
  }, []);

  const handleImageLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    dispatch({ type: 'CLEAR_HOVER_PREVIEW' });
  }, []);

  // Open the full-size lightbox when a thumbnail is clicked
  const handleImageExpand = useCallback((url: string, name: string, category: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    dispatch({ type: 'OPEN_LIGHTBOX', payload: { url, name, category } });
  }, []);

  const handleLightboxClose = useCallback(() => {
    dispatch({ type: 'CLOSE_LIGHTBOX' });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="form-label flex items-center gap-2">
          <Palette className="h-4 w-4" />
          {t('image.styles.label', { defaultValue: 'Styles' })}
          <span className="text-white/40 font-normal">
            ({t('common.optional', { defaultValue: 'optional' })})
          </span>
        </label>
        {safeSelectedStyles.length > 0 && (
          <button
            type="button"
            onClick={clearAllStyles}
            className="text-xs text-white/40 hover:text-white/60"
            disabled={disabled}
          >
            {t('common.clear', { defaultValue: 'Clear' })}
          </button>
        )}
      </div>

      {/* Selected Styles */}
      {safeSelectedStyles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {safeSelectedStyles.map((styleName) => {
            const styleInfo = styles.find((s) => s.name === styleName);
            const previewUrl = styleInfo?.preview_url ? buildApiUrl(styleInfo.preview_url) : null;
            return (
              <span
                key={styleName}
                className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 bg-primary-900/30 text-primary-300 text-sm rounded-full border border-primary-500/50"
              >
                {previewUrl ? (
                  <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={previewUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-primary-900/50 flex items-center justify-center flex-shrink-0">
                    <Palette className="h-3 w-3 text-primary-400" />
                  </div>
                )}
                <span className="whitespace-nowrap">{styleName}</span>
                <button
                  type="button"
                  onClick={() => removeStyle(styleName)}
                  className="hover:bg-primary-800/50 rounded-full p-0.5"
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Expand/Collapse Button */}
      <button
        type="button"
        onClick={() => dispatch({ type: 'TOGGLE_EXPANDED' })}
        className="flex items-center gap-2 w-full p-3 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
        disabled={disabled || isLoading}
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        <span className="text-sm text-white/60">
          {isExpanded
            ? t('image.styles.collapse', { defaultValue: 'Hide styles' })
            : t('image.styles.expand', {
                defaultValue: 'Browse {{count}} styles',
                count: styles.length,
              })}
        </span>
        {safeSelectedStyles.length > 0 && (
          <span className="ml-auto text-sm text-primary-400 font-medium">
            {safeSelectedStyles.length}/{maxStyles}
          </span>
        )}
      </button>

      {/* Style Browser */}
      {isExpanded && (
        <div className="space-y-3 p-4 border border-white/10 rounded-lg bg-black/30 animate-in slide-in-from-top-2 duration-200">
          {/* Search and Favorites Filter */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value })}
              placeholder={t('image.styles.search', { defaultValue: 'Search styles...' })}
              className="input flex-1"
              disabled={disabled}
            />
            <button
              type="button"
              onClick={() => dispatch({ type: 'TOGGLE_SHOW_ONLY_FAVORITES' })}
              className={`p-2 rounded-lg border transition-colors ${
                showOnlyFavorites
                  ? 'bg-red-900/30 border-red-500/50 text-red-400'
                  : 'border-white/10 text-white/40 hover:text-red-400 hover:border-red-500/30'
              }`}
              title={t('image.styles.favorites', { defaultValue: 'Show favorites only' })}
              disabled={disabled}
            >
              <Heart className={`h-4 w-4 ${showOnlyFavorites ? 'fill-current' : ''}`} />
            </button>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => dispatch({ type: 'SET_ACTIVE_CATEGORY', payload: null })}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors border ${
                activeCategory === null
                  ? 'bg-primary-900/30 border-primary-500 text-primary-200'
                  : 'bg-white/10 border-white/10 text-white/60 hover:bg-white/15'
              }`}
              disabled={disabled}
            >
              {t('image.styles.all', { defaultValue: 'All' })}
            </button>
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => dispatch({ type: 'SET_ACTIVE_CATEGORY', payload: category })}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors capitalize border ${
                  activeCategory === category
                    ? 'bg-primary-900/30 border-primary-500 text-primary-200'
                    : 'bg-white/10 border-white/10 text-white/60 hover:bg-white/15'
                }`}
                disabled={disabled}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Style Grid with Virtual Scrolling */}
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent"
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {visibleStyles.map((style) => {
                const isSelected = safeSelectedStyles.includes(style.name);
                const isFavorite = favorites.includes(style.name);
                const canSelect = safeSelectedStyles.length < maxStyles;

                return (
                  <button
                    key={style.name}
                    type="button"
                    onClick={() => toggleStyle(style.name)}
                    className={`relative flex items-center gap-2 p-2 text-sm rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'border-primary-500 bg-primary-900/30 text-primary-300 ring-1 ring-primary-500/50'
                        : canSelect
                          ? 'border-white/10 hover:border-white/20 hover:bg-white/5'
                          : 'border-white/5 opacity-50 cursor-not-allowed'
                    }`}
                    disabled={disabled || (!isSelected && !canSelect)}
                  >
                    <StylePreviewImage
                      previewUrl={style.preview_url}
                      styleName={style.name}
                      isSelected={isSelected}
                      onHover={(url, rect) => handleImageHover(url, rect, style.name)}
                      onLeave={handleImageLeave}
                      onExpand={(url) => handleImageExpand(url, style.name, style.category)}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="block truncate text-xs font-medium text-white/80">
                        {style.name}
                      </span>
                      {isSelected && <Check className="h-3 w-3 text-primary-400 mt-0.5" />}
                    </div>
                    {/* Favorite button */}
                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(style.name, e)}
                      className={`p-0.5 rounded hover:bg-white/10 transition-colors ${
                        isFavorite ? 'text-red-500' : 'text-white/20 hover:text-red-400'
                      }`}
                      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart className={`h-3 w-3 ${isFavorite ? 'fill-current' : ''}`} />
                    </button>
                  </button>
                );
              })}
            </div>

            {/* Load more indicator */}
            {visibleCount < filteredStyles.length && (
              <div className="flex justify-center py-4">
                <p className="text-xs text-white/40">
                  {t('image.styles.showing', {
                    defaultValue: 'Showing {{visible}} of {{total}} styles',
                    visible: visibleCount,
                    total: filteredStyles.length,
                  })}
                  {' - '}
                  {t('image.styles.scrollMore', { defaultValue: 'scroll for more' })}
                </p>
              </div>
            )}

            {filteredStyles.length === 0 && (
              <p className="text-center text-white/40 py-4">
                {t('image.styles.noResults', { defaultValue: 'No styles found' })}
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-white/40">
        {t('image.styles.hint', {
          defaultValue: 'Select up to {{max}} styles to influence the image aesthetics',
          max: maxStyles,
        })}
      </p>

      {/* Hover Preview Portal */}
      {hoverPreview && (
        <HoverPreview
          imageUrl={hoverPreview.url}
          styleName={hoverPreview.name}
          position={hoverPreview.position}
        />
      )}

      {/* Full-size Lightbox (click to open) */}
      {lightbox && (
        <StyleLightbox
          imageUrl={lightbox.url}
          styleName={lightbox.name}
          category={lightbox.category}
          onClose={handleLightboxClose}
        />
      )}
    </div>
  );
});

export default StyleSelector;
